/**
 * 删除确认 + 「这个字能组哪些词」浮层。
 *
 * 删除一律要确认：字/词删掉之后**拼音覆盖与错词关联也没了**（第三步的错词表会挂在
 * 字/词上），家长在手机上一误触就要重新录一遍。文案要把他删的是什么说清楚，
 * 而不是一句"确定删除吗"。
 */
import { useMemo, useState } from 'react';
import { Badge, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { wordCategoryLabel } from '../../lib/api/dictation';
import type { HanziView, WordView } from '../../lib/api/dictation';

// ---------------------------------------------------------------- 删除确认

export interface DeleteTarget {
  kind: 'hanzi' | 'word' | 'lesson';
  id: string;
  /** 显示用的名字（字 / 词 / 课题）。 */
  label: string;
  /** 附带说明（例如课文的"字与词本身会保留"）。 */
  detail: string;
}

export function DeleteConfirmModal({
  target,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  target: DeleteTarget | null;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={target !== null}
      title={
        target === null
          ? '删除'
          : target.kind === 'hanzi'
            ? '删除这个字'
            : target.kind === 'word'
              ? '删除这个词'
              : '删除这节课文'
      }
      onClose={onCancel}
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" disabled={deleting} onClick={onCancel}>
            取消
          </Button>
          <Button variant="danger" loading={deleting} onClick={onConfirm}>
            确认删除
          </Button>
        </div>
      }
    >
      <p className="text-sm text-slate-700">
        要删除 <strong className="text-lg">{target?.label ?? ''}</strong> 吗？
      </p>
      <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">{target?.detail ?? ''}</p>
      {error !== null && (
        <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- 组词

export function HanziWordsModal({
  open,
  hanzi,
  words,
  loading,
  missing,
  error,
  onRetry,
  onCreateWord,
  onClose,
}: {
  open: boolean;
  hanzi: HanziView | null;
  words: WordView[] | null;
  loading: boolean;
  /** 响应里没有 `words` 数组（字段名不符）—— 与"这个词一个字都组不出来"是两回事。 */
  missing: boolean;
  error: string | null;
  onRetry: () => void;
  /**
   * 「给这个字录词语 / 成语」。
   *
   * ⚠️ 2026-10-16 起这是**唯一的人工造词入口**：导入里不再允许"顺手写词"
   *    （识字表/写字表只能录单个汉字）。所以它必须**显眼** ——
   *    放在弹框底部的主按钮位，而不是藏在某个小图标里。
   *    ⚠️ 传 `undefined` 时（家长只读模式）**整个按钮不渲染**，
   *    而不是渲染一个点了没反应的灰按钮。
   */
  onCreateWord?: (() => void) | undefined;
  onClose: () => void;
}) {
  /** 空串读音要能看出来（内置字表没这个字），而不是留白。 */
  const pinyinText = useMemo(() => {
    if (hanzi === null) return '';
    return hanzi.pinyin === '' ? '（内置字表里没有这个字）' : hanzi.pinyin;
  }, [hanzi]);

  return (
    <Modal
      open={open}
      title={hanzi === null ? '组词' : `「${hanzi.charText}」能组哪些词`}
      description={hanzi === null ? undefined : `读音：${pinyinText}`}
      onClose={onClose}
      size="md"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            关闭
          </Button>
          {/*
            ⚠️ 这个按钮就是"点字 → 弹框 → 录词"里的那一步（用户明确要的造词入口）。
               文案写全（"录词语 / 成语"），因为它同时承担"告诉家长这里能做什么"。
          */}
          {onCreateWord !== undefined && hanzi !== null && (
            <Button onClick={onCreateWord}>给这个字录词语 / 成语</Button>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : error !== null ? (
        <div className="space-y-3">
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
          <Button size="sm" variant="secondary" onClick={onRetry}>
            重试
          </Button>
        </div>
      ) : missing ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          没拿到组词列表（后端返回里没有 `words` 数组）—— 这不是"这个字组不出词"。
        </p>
      ) : words === null || words.length === 0 ? (
        <EmptyState
          icon="🔤"
          title="还没有用这个字组的词"
          description={
            onCreateWord === undefined
              ? '导入词语时会自动建立「字 ↔ 词」关联。'
              : '点下面的「给这个字录词语 / 成语」就能录 —— 这个词可以不在课本词语表里（家长自己造的常用词也算）。'
          }
        />
      ) : (
        <ul className="space-y-1.5">
          {words.map((word) => (
            <li
              key={word.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <span className="text-base font-semibold text-slate-900">{word.wordText}</span>
                <span className="ml-2 font-mono text-sm text-slate-500">
                  {word.pinyin === '' ? '（没有读音）' : word.pinyin}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Badge tone={word.category === 'idiom' ? 'success' : 'neutral'}>
                  {wordCategoryLabel(word.category)}
                </Badge>
                {word.pinyinOverridden && <Badge tone="warn">改过拼音</Badge>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {/* 有词的时候也要说清"还能再加"（这个词可以不在课本词语表里）。 */}
      {!loading && error === null && !missing && words !== null && words.length > 0 && onCreateWord !== undefined && (
        <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
          还想加？点下面的「给这个字录词语 / 成语」——录的词<strong>可以不在课本词语表里</strong>
          （自己造的常用词也算），保存后会自动关联到这个字。
        </p>
      )}
    </Modal>
  );
}

/** 一个极小的受控开关（给页面做"点开组词"用，避免页面里写一堆 useState 名字）。 */
export function useModalTarget<T>(): {
  target: T | null;
  open: (value: T) => void;
  close: () => void;
} {
  const [target, setTarget] = useState<T | null>(null);
  return {
    target,
    open: (value: T) => setTarget(value),
    close: () => setTarget(null),
  };
}
